/**
 * React Query hooks for API operations
 * TanStack Query (React Query) ile API çağrıları için custom hooks
 */

import { 
  useQuery, 
  useMutation, 
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
  QueryKey,
  MutationFunction,
  UseInfiniteQueryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { 
  PaginatedResponse, 
  ApiErrorResponse,
  BaseQueryParams,
  ID
} from '../types/api.types';
import type { NotificationListRequest } from '../types/requests/notification.types';

import services from '../services';

/**
 * Query key factory - Type-safe query key oluşturma
 */
export class QueryKeyFactory {
  // Auth
  static readonly auth = {
    profile: ['auth', 'profile'] as const,
    session: ['auth', 'session'] as const
  };

  // Alarms
  static readonly alarms = {
    all: ['alarms'] as const,
    lists: () => [...QueryKeyFactory.alarms.all, 'list'] as const,
    list: (params?: BaseQueryParams) => [...QueryKeyFactory.alarms.lists(), params] as const,
    details: () => [...QueryKeyFactory.alarms.all, 'detail'] as const,
    detail: (id: ID) => [...QueryKeyFactory.alarms.details(), id] as const,
    statistics: ['alarms', 'statistics'] as const,
    active: ['alarms', 'active'] as const,
    recent: (limit?: number) => ['alarms', 'recent', limit] as const
  };

  // Units
  static readonly units = {
    all: ['units'] as const,
    lists: () => [...QueryKeyFactory.units.all, 'list'] as const,
    list: (params?: BaseQueryParams) => [...QueryKeyFactory.units.lists(), params] as const,
    details: () => [...QueryKeyFactory.units.all, 'detail'] as const,
    detail: (id: ID) => [...QueryKeyFactory.units.details(), id] as const,
    performance: (id: ID) => ['units', id, 'performance'] as const,
    alarms: (id: ID) => ['units', id, 'alarms'] as const,
    wagons: (id: ID) => ['units', id, 'wagons'] as const,
    attentionRequired: ['units', 'attention-required'] as const
  };

  // Fleets
  static readonly fleets = {
    all: ['fleets'] as const,
    lists: () => [...QueryKeyFactory.fleets.all, 'list'] as const,
    list: (params?: BaseQueryParams) => [...QueryKeyFactory.fleets.lists(), params] as const,
    details: () => [...QueryKeyFactory.fleets.all, 'detail'] as const,
    detail: (id: ID) => [...QueryKeyFactory.fleets.details(), id] as const
  };

  // Wagons
  static readonly wagons = {
    all: ['wagons'] as const,
    lists: () => [...QueryKeyFactory.wagons.all, 'list'] as const,
    list: (params?: BaseQueryParams) => [...QueryKeyFactory.wagons.lists(), params] as const,
    details: () => [...QueryKeyFactory.wagons.all, 'detail'] as const,
    detail: (id: ID) => [...QueryKeyFactory.wagons.details(), id] as const
  };

  // Users
  static readonly users = {
    all: ['users'] as const,
    lists: () => [...QueryKeyFactory.users.all, 'list'] as const,
    list: (params?: BaseQueryParams) => [...QueryKeyFactory.users.lists(), params] as const,
    details: () => [...QueryKeyFactory.users.all, 'detail'] as const,
    detail: (id: ID) => [...QueryKeyFactory.users.details(), id] as const
  };

  // Tenants
  static readonly tenants = {
    all: ['tenants'] as const,
    lists: () => [...QueryKeyFactory.tenants.all, 'list'] as const,
    list: (params?: BaseQueryParams) => [...QueryKeyFactory.tenants.lists(), params] as const,
    details: () => [...QueryKeyFactory.tenants.all, 'detail'] as const,
    detail: (id: ID) => [...QueryKeyFactory.tenants.details(), id] as const
  };

  // Buildings
  static readonly buildings = {
    all: ['buildings'] as const,
    lists: () => [...QueryKeyFactory.buildings.all, 'list'] as const,
    list: (params?: BaseQueryParams) => [...QueryKeyFactory.buildings.lists(), params] as const,
    details: () => [...QueryKeyFactory.buildings.all, 'detail'] as const,
    detail: (id: ID) => [...QueryKeyFactory.buildings.details(), id] as const
  };

  // Notifications
  static readonly notifications = {
    all: ['notifications'] as const,
    lists: () => [...QueryKeyFactory.notifications.all, 'list'] as const,
    list: (params?: BaseQueryParams) => [...QueryKeyFactory.notifications.lists(), params] as const,
    unread: ['notifications', 'unread'] as const
  };
}

/**
 * Query options factory - Standart query ayarları
 */
export const defaultQueryOptions = <TData, TError = ApiErrorResponse>(
  options?: Partial<UseQueryOptions<TData, TError>>
): Partial<UseQueryOptions<TData, TError>> => ({
  retry: (failureCount, error: any) => {
    // 401 ve 403 hatalarında retry yapma
    if (error?.status === 401 || error?.status === 403) {
      return false;
    }
    return failureCount < 2;
  },
  retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  staleTime: 5 * 60 * 1000, // 5 dakika
  gcTime: 10 * 60 * 1000, // 10 dakika (cache time)
  refetchOnWindowFocus: false,
  refetchOnMount: true,
  refetchOnReconnect: true,
  ...options
});

export const defaultInfiniteQueryOptions = <TData, TError = ApiErrorResponse>(
  options?: Partial<UseInfiniteQueryOptions<TData, TError>>
): Partial<UseInfiniteQueryOptions<TData, TError>> => ({
  retry: (failureCount, error: any) => {
    if (error?.status === 401 || error?.status === 403) {
      return false;
    }
    // Infinite query için daha az retry
    return failureCount < 1;
  },
  retryDelay: (attemptIndex) => Math.min(1500 * 2 ** attemptIndex, 25000),
  staleTime: 3 * 60 * 1000, // 3 dakika (normalden daha kısa)
  gcTime: 8 * 60 * 1000, // 8 dakika
  refetchOnWindowFocus: true, // Farklı
  refetchOnMount: false, // Farklı
  refetchOnReconnect: false, // Farklı
  ...options
});
/**
 * Mutation options factory - Standart mutation ayarları
 */
export const defaultMutationOptions = <TData, TVariables, TError = ApiErrorResponse>(
  options?: Partial<UseMutationOptions<TData, TError, TVariables>>
): Partial<UseMutationOptions<TData, TError, TVariables>> => ({
  retry: false,
  ...options
});

/**
 * Generic API query hook
 */
export function useApiQuery<TData, TError = ApiErrorResponse>(
  queryKey: QueryKey,
  queryFn: () => Promise<TData>,
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TData, TError>({
    queryKey,
    queryFn,
    ...defaultQueryOptions(options)
  });
}

/**
 * Generic API mutation hook
 */
export function useApiMutation<TData = void, TVariables = void, TError = ApiErrorResponse>(
  mutationFn: MutationFunction<TData, TVariables>,
  options?: UseMutationOptions<TData, TError, TVariables>
) {
  return useMutation<TData, TError, TVariables>({
    mutationFn,
    ...defaultMutationOptions(options)
  });
}

/**
 * Infinite query için generic hook
 */
export function useApiInfiniteQuery<TData, TError = ApiErrorResponse>(
  queryKey: QueryKey,
  queryFn: (context: { pageParam: number }) => Promise<PaginatedResponse<TData>>,
  options?: Omit<UseInfiniteQueryOptions<PaginatedResponse<TData>, TError>, 'queryKey' | 'queryFn'>
) {
  return useInfiniteQuery({
    queryKey,
    queryFn: (context) => queryFn({ pageParam: context.pageParam as number }),
    getNextPageParam: (lastPage) => {
      if (lastPage.meta?.hasNext) {
        return (lastPage.meta.page || 1) + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    ...options
  });
}

// ==================== AUTH HOOKS ====================

/**
 * Current user profile hook
 */
export function useAuthProfile() {
  return useApiQuery(
    QueryKeyFactory.auth.profile,
    () => services.auth.getProfile(),
    {
      staleTime: 10 * 60 * 1000, // 10 dakika
      enabled: !!services.auth.getAccessToken()
    } 
  );
}

/**
 * Login mutation hook
 */
export function useLogin() {
  const queryClient = useQueryClient();

  return useApiMutation(
    (credentials: { email: string; password: string; rememberMe?: boolean; deviceInfo?: any }) => 
      services.auth.login(credentials),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeyFactory.auth.profile });
        queryClient.invalidateQueries({ queryKey: QueryKeyFactory.alarms.all });
        queryClient.invalidateQueries({ queryKey: QueryKeyFactory.units.all });
      }
    }
  );
}

/**
 * Logout mutation hook
 */
export function useLogout() {
  const queryClient = useQueryClient();

  return useApiMutation(
    () => services.auth.logout(),
    {
      onSuccess: () => {
        // Tüm queries'i temizle
        queryClient.clear();
      }
    }
  );
}

// ==================== ALARM HOOKS ====================

/**
 * Alarms list hook
 */
export function useAlarms(params?: BaseQueryParams) {
  return useApiQuery(
    QueryKeyFactory.alarms.list(params),
    () => services.alarm.getAlarms(params),
    {
      // Alarms sık değişebilir, daha kısa stale time
      staleTime: 30 * 1000, // 30 saniye
    }
  );
}

/**
 * Alarm detail hook
 */
export function useAlarm(id: ID) {
  return useApiQuery(
    QueryKeyFactory.alarms.detail(id),
    () => services.alarm.getAlarm(id),
    {
      staleTime: 60 * 1000, // 1 dakika
      enabled: !!id
    }
  );
}

/**
 * Active alarms hook
 */
export function useActiveAlarms(params?: BaseQueryParams) {
  return useApiQuery(
    ['alarms', 'active', params],
    () => services.alarm.getActiveAlarms(params),
    {
      staleTime: 15 * 1000, // 15 saniye (active alarms sık değişir)
      refetchInterval: 30 * 1000, // 30 saniyede bir otomatik refetch
    }
  );
}

/**
 * Recent alarms hook
 */
export function useRecentAlarms(limit: number = 10) {
  return useApiQuery(
    QueryKeyFactory.alarms.recent(limit),
    () => services.alarm.getRecentAlarms(limit),
    {
      staleTime: 15 * 1000,
      refetchInterval: 30 * 1000,
    }
  );
}

/**
 * Alarm statistics hook
 */
export function useAlarmStatistics(params?: BaseQueryParams) {
  return useApiQuery(
    QueryKeyFactory.alarms.statistics,
    () => services.alarm.getAlarmStatistics(params),
    {
      staleTime: 60 * 1000, // 1 dakika
    }
  );
}

/**
 * Acknowledge alarms mutation
 */
export function useAcknowledgeAlarms() {
  const queryClient = useQueryClient();

  return useApiMutation(
    (request: { alarmIds: string[]; acknowledgedBy: string; comment?: string }) => 
      services.alarm.acknowledgeAlarms(request),
    {
      onSuccess: (_, variables) => {
        for (const id of variables.alarmIds) {
            queryClient.invalidateQueries({ queryKey: QueryKeyFactory.alarms.detail(id) });
        }
        
        queryClient.invalidateQueries({ queryKey: QueryKeyFactory.alarms.lists() });
        queryClient.invalidateQueries({ queryKey: QueryKeyFactory.alarms.active });
        queryClient.invalidateQueries({ queryKey: QueryKeyFactory.alarms.recent() });
      }
    }
  );
}

/**
 * Resolve alarms mutation
 */
export function useResolveAlarms() {
  const queryClient = useQueryClient();

  return useApiMutation(
    (request: { alarmIds: string[]; resolvedBy: string; resolution?: string; comment?: string }) => 
      services.alarm.resolveAlarms(request),
    {
      onSuccess: (_, variables) => {
        for (const id of variables.alarmIds) {
          queryClient.invalidateQueries({ queryKey: QueryKeyFactory.alarms.detail(id) });
        }
        
        queryClient.invalidateQueries({ queryKey: QueryKeyFactory.alarms.lists() });
        queryClient.invalidateQueries({ queryKey: QueryKeyFactory.alarms.active });
      }
    }
  );
}

// ==================== UNIT HOOKS ====================

/**
 * Units list hook
 */
export function useUnits(params?: BaseQueryParams) {
  return useApiQuery(
    QueryKeyFactory.units.list(params),
    () => services.unit.getUnits(params),
    {
      staleTime: 60 * 1000, // 1 dakika
    }
  );
}

/**
 * Unit detail hook
 */
export function useUnit(id: ID) {
  return useApiQuery(
    QueryKeyFactory.units.detail(id),
    () => services.unit.getUnit(id),
    {
      staleTime: 2 * 60 * 1000, // 2 dakika
      enabled: !!id
    }
  );
}

/**
 * Units requiring attention hook
 */
export function useUnitsRequiringAttention(params?: BaseQueryParams) {
  return useApiQuery(
    QueryKeyFactory.units.attentionRequired,
    () => services.unit.getUnitsRequiringAttention(params),
    {
      staleTime: 30 * 1000, // 30 saniye
      refetchInterval: 60 * 1000, // 1 dakikada bir refetch
    }
  );
}

/**
 * Unit performance hook
 */
export function useUnitPerformance(id: ID) {
  return useApiQuery(
    QueryKeyFactory.units.performance(id),
    () => services.unit.getUnitPerformance(id),
    {
      staleTime: 60 * 1000,
      enabled: !!id
    }
  );
}

/**
 * Unit alarms hook
 */
export function useUnitAlarms(unitId: ID, params?: BaseQueryParams) {
  return useApiQuery(
    QueryKeyFactory.units.alarms(unitId),
    () => services.unit.getUnitAlarms(unitId, params),
    {
      staleTime: 30 * 1000,
      enabled: !!unitId
    }
  );
}

/**
 * Unit wagons hook
 */
export function useUnitWagons(unitId: ID) {
  return useApiQuery(
    QueryKeyFactory.units.wagons(unitId),
    () => services.unit.getUnitWagons(unitId),
    {
      staleTime: 5 * 60 * 1000, // 5 dakika (wagon bilgisi sık değişmez)
      enabled: !!unitId
    }
  );
}

/**
 * Update unit mutation
 */
export function useUpdateUnit() {
  const queryClient = useQueryClient();

  return useApiMutation(
    ({ id, data }: { id: ID; data: any }) => services.unit.updateUnit(id, data),
    {
      onSuccess: (data, variables) => {
        // Updated unit'in cache'ini güncelle
        queryClient.setQueryData(QueryKeyFactory.units.detail(variables.id), data);
        
        // Unit list'lerini invalidate et
        queryClient.invalidateQueries({ queryKey: QueryKeyFactory.units.lists() });
      }
    }
  );
}

// ==================== FLEET HOOKS ====================

/**
 * Fleets list hook
 */
export function useFleets(params?: BaseQueryParams) {
  return useApiQuery(
    QueryKeyFactory.fleets.list(params),
    () => services.fleet.getFleets(params),
    {
      staleTime: 2 * 60 * 1000, // 2 dakika
    }
  );
}

/**
 * Fleet detail hook
 */
export function useFleet(id: ID) {
  return useApiQuery(
    QueryKeyFactory.fleets.detail(id),
    () => services.fleet.getFleet(id),
    {
      staleTime: 2 * 60 * 1000,
      enabled: !!id
    }
  );
}

// ==================== WAGON HOOKS ====================

/**
 * Wagons list hook
 */
export function useWagons(params?: BaseQueryParams) {
  return useApiQuery(
    QueryKeyFactory.wagons.list(params),
    () => services.wagon.getWagons(params),
    {
      staleTime: 2 * 60 * 1000,
    }
  );
}

/**
 * Wagon detail hook
 */
export function useWagon(id: ID) {
  return useApiQuery(
    QueryKeyFactory.wagons.detail(id),
    () => services.wagon.getWagon(id),
    {
      staleTime: 2 * 60 * 1000,
      enabled: !!id
    }
  );
}

// ==================== USER HOOKS ====================

/**
 * Users list hook
 */
export function useUsers(params?: BaseQueryParams) {
  return useApiQuery(
    QueryKeyFactory.users.list(params),
    () => services.user.getUsers(params),
    {
      staleTime: 5 * 60 * 1000, // 5 dakika
    }
  );
}

/**
 * User detail hook
 */
export function useUser(id: ID) {
  return useApiQuery(
    QueryKeyFactory.users.detail(id),
    () => services.user.getUser(id),
    {
      staleTime: 5 * 60 * 1000,
      enabled: !!id
    }
  );
}

// ==================== NOTIFICATION HOOKS ====================

/**
 * Notifications list hook
 */
export function useNotifications(params?: NotificationListRequest) {
  return useApiQuery(
    QueryKeyFactory.notifications.list(params),
    () => services.notification.getNotifications(params),
    {
      staleTime: 30 * 1000,
      refetchInterval: 60 * 1000,
    }
  );
}

/**
 * Unread notifications hook
 */
export function useUnreadNotifications() {
  return useApiQuery(
    QueryKeyFactory.notifications.unread,
    () => services.notification.getUnreadNotifications(),
    {
      staleTime: 15 * 1000,
      refetchInterval: 30 * 1000,
    }
  );
}

/**
 * Mark notification as read mutation
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useApiMutation(
    (id: ID) => services.notification.markAsRead(id),
    {
      onSuccess: () => {
        // Notification queries'ini invalidate et
        queryClient.invalidateQueries({ queryKey: QueryKeyFactory.notifications.lists() });
        queryClient.invalidateQueries({ queryKey: QueryKeyFactory.notifications.unread });
      }
    }
  );
}

// ==================== INFINITE QUERY HOOKS ====================

/**
 * Infinite scroll için alarms hook
 */
export function useInfiniteAlarms(params?: BaseQueryParams) {
  return useApiInfiniteQuery(
    ['alarms', 'infinite', params],
    ({ pageParam = 1 }) => services.alarm.getAlarms({
      ...params,
      page: pageParam,
      limit: params?.limit || 20
    })
  );
}

/**
 * Infinite scroll için units hook
 */
export function useInfiniteUnits(params?: BaseQueryParams) {
  return useApiInfiniteQuery(
    ['units', 'infinite', params],
    ({ pageParam = 1 }) => services.unit.getUnits({
      ...params,
      page: pageParam,
      limit: params?.limit || 20
    })
  );
}

// ==================== OPTIMISTIC UPDATE HOOKS ====================

/**
 * Optimistic update ile alarm acknowledge hook
 */
export function useOptimisticAcknowledgeAlarms() {
  const queryClient = useQueryClient();

  return useApiMutation(
    (request: { alarmIds: string[]; acknowledgedBy: string; comment?: string }) => 
      services.alarm.acknowledgeAlarms(request),
    {
      onMutate: async (request) => {
        // Önceki state'i kaydet
        const previousAlarms = queryClient.getQueryData(QueryKeyFactory.alarms.lists());
        
        for (const id of request.alarmIds) {
          queryClient.setQueryData(QueryKeyFactory.alarms.detail(id), (old: any) => ({
            ...old,
            status: 'acknowledged',
            acknowledgedAt: new Date().toISOString()
          }));
        }

        return { previousAlarms };
      },
      
      onError: (err, variables, context: any ) => {
        if (context?.previousAlarms) {
          queryClient.setQueryData(QueryKeyFactory.alarms.lists(), context.previousAlarms);
        }
      },
      
      onSettled: () => {
        // İşlem tamamlandığında veriyi tekrar getir
        queryClient.invalidateQueries({ queryKey: QueryKeyFactory.alarms.lists() });
      }
    }
  );
}

// ==================== QUERY UTILITIES ====================

/**
 * Query client utilities
 */
export function useQueryUtils() {
  const queryClient = useQueryClient();

  return {
    /**
     * Belirli bir query'nin cache'ini temizle
     */
    invalidateQuery: (queryKey: QueryKey) => {
      queryClient.invalidateQueries({ queryKey });
    },

    /**
     * Belirli bir query'nin data'sını set et
     */
    setQueryData: <T>(queryKey: QueryKey, data: T) => {
      queryClient.setQueryData(queryKey, data);
    },

    /**
     * Belirli bir query'nin data'sını getir
     */
    getQueryData: <T>(queryKey: QueryKey): T | undefined => {
      return queryClient.getQueryData(queryKey);
    },

    /**
     * Tüm queries'i temizle
     */
    clearAllQueries: () => {
      queryClient.clear();
    },

    /**
     * Tüm aktif queries'i cancel et
     */
    cancelAllQueries: () => {
      queryClient.cancelQueries();
    },

    /**
     * Query cache'ini temizle ama aktif queries'i koru
     */
    clearQueryCache: () => {
      queryClient.removeQueries();
    }
  };
}

/**
 * Prefetch hook'ları
 */
export function usePrefetch() {
  const queryClient = useQueryClient();

  return {
    /**
     * Unit detail prefetch
     */
    prefetchUnit: (id: ID) => {
      queryClient.prefetchQuery({
        queryKey: QueryKeyFactory.units.detail(id),
        queryFn: () => services.unit.getUnit(id)
      });
    },

    /**
     * Alarm detail prefetch
     */
    prefetchAlarm: (id: ID) => {
      queryClient.prefetchQuery({
        queryKey: QueryKeyFactory.alarms.detail(id),
        queryFn: () => services.alarm.getAlarm(id)
      });
    },

    /**
     * User profile prefetch
     */
    prefetchProfile: () => {
      queryClient.prefetchQuery({
        queryKey: QueryKeyFactory.auth.profile,
        queryFn: () => services.auth.getProfile()
      });
    }
  };
}

// ==================== ERROR HANDLING ====================

/**
 * Query error handler hook
 */
export function useQueryErrorHandler() {
  const handleError = (error: ApiErrorResponse) => {
    // 401 Unauthorized - otomatik logout
    if (error.status === 401) {
      services.auth.clearSession();
      globalThis.location.href = '/login';
      return;
    }

    // 403 Forbidden - yetki hatası
    if (error.status === 403) {
      // Toast göster veya notification
      const event = new CustomEvent('show-toast', {
        detail: {
          type: 'error',
          title: 'Access Denied',
          message: 'You do not have permission to perform this action.'
        }
      });
      globalThis.dispatchEvent(event);
      return;
    }

    // Diğer hatalar
    console.error('API Query Error:', error);
    
    const event = new CustomEvent('show-toast', {
      detail: {
        type: 'error',
        title: 'Error',
        message: error.message || 'An error occurred'
      }
    });
    globalThis.dispatchEvent(event);
  };

  return { handleError };
}

// ==================== DEFAULT EXPORT ====================

/**
 * Tüm hook'ları içeren ana nesne
 */
const apiHooks = {
  // Auth
  useAuthProfile,
  useLogin,
  useLogout,

  // Alarms
  useAlarms,
  useAlarm,
  useActiveAlarms,
  useRecentAlarms,
  useAlarmStatistics,
  useAcknowledgeAlarms,
  useResolveAlarms,
  useOptimisticAcknowledgeAlarms,

  // Units
  useUnits,
  useUnit,
  useUnitsRequiringAttention,
  useUnitPerformance,
  useUnitAlarms,
  useUnitWagons,
  useUpdateUnit,

  // Fleets
  useFleets,
  useFleet,

  // Wagons
  useWagons,
  useWagon,

  // Users
  useUsers,
  useUser,

  // Notifications
  useNotifications,
  useUnreadNotifications,
  useMarkNotificationAsRead,

  // Infinite Queries
  useInfiniteAlarms,
  useInfiniteUnits,

  // Utilities
  useQueryUtils,
  usePrefetch,
  useQueryErrorHandler,

  // Core
  useApiQuery,
  useApiMutation,
  useApiInfiniteQuery,
  
  // Factories
  QueryKeyFactory,
  defaultQueryOptions,
  defaultMutationOptions
};

export default apiHooks;